import {FC, useEffect, useMemo, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {ArrowDownward, ArrowUpward, Build, Download, Edit as EditIcon, Public, Refresh, RotateLeft, Stop, Upload} from '@mui/icons-material';
import {useNavigate} from 'react-router-dom';
import {
    useBackfillDictionaryMetadata,
    useCancelDictionaryBackfillJob,
    useDictionaryBackfillJobs,
    useDictionaryWords,
    useExportDictionaryWords,
    useBulkUpsertDictionaryWords,
    useUpdateDictionaryWord,
    usePreviewRegeneratePhoneticUs,
    useRegeneratePhoneticUs,
    useResetDictionaryBackfillAttempts,
} from '../../hooks/useWord.ts';
import {DictionaryMetadataBackfillJob, DictionaryWordItem} from "../../types/dictionary.ts";
import {CsvUploadDialog, CsvColumnDef, generateCsv, downloadCsv} from '../common/CsvUploadDialog.tsx';
import { formatKstDateTime } from '../../utils/dateTime';

export const DictionaryWordList: FC = () => {
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [wordInput, setWordInput] = useState('');
    const [languageCodeInput, setLanguageCodeInput] = useState('');
    const [wordFilter, setWordFilter] = useState('');
    const [languageCodeFilter, setLanguageCodeFilter] = useState('');
    const [onlyMissing, setOnlyMissing] = useState(true);
    const [backfillExhausted, setBackfillExhausted] = useState(false);
    const [sortField, setSortField] = useState('id');
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
    const [sourceType, setSourceType] = useState('');
    const [createdJob, setCreatedJob] = useState<DictionaryMetadataBackfillJob | null>(null);
    const [csvDialogOpen, setCsvDialogOpen] = useState(false);
    const [regenDialogOpen, setRegenDialogOpen] = useState(false);
    const [regenLimit, setRegenLimit] = useState('200');
    const [regenBatchSize, setRegenBatchSize] = useState('50');
    const [editOpen, setEditOpen] = useState(false);
    const [editRow, setEditRow] = useState<DictionaryWordItem | null>(null);
    const [editTranslation, setEditTranslation] = useState('');
    const [editPos, setEditPos] = useState('');
    const [editDifficulty, setEditDifficulty] = useState<number | ''>('');

    const params = useMemo(() => ({
        page: page + 1,
        size,
        onlyMissing,
        backfillExhausted: backfillExhausted || undefined,
        word: wordFilter || undefined,
        languageCode: languageCodeFilter || undefined,
        sourceType: sourceType || undefined,
        sort: sortField,
        direction: sortDirection,
    }), [page, size, onlyMissing, backfillExhausted, wordFilter, languageCodeFilter, sourceType, sortField, sortDirection]);

    const {data, isLoading, error, refetch} = useDictionaryWords(params);
    const backfill = useBackfillDictionaryMetadata();
    const cancelBackfill = useCancelDictionaryBackfillJob();
    const previewRegen = usePreviewRegeneratePhoneticUs();
    const regen = useRegeneratePhoneticUs();
    const exportMutation = useExportDictionaryWords();
    const upsertMutation = useBulkUpsertDictionaryWords();
    const updateMutation = useUpdateDictionaryWord();
    const resetBackfillMutation = useResetDictionaryBackfillAttempts();
    const {
        data: jobsData,
        isLoading: jobsLoading,
        error: jobsError,
        refetch: refetchJobs,
    } = useDictionaryBackfillJobs(1, 20, true);
    const runningJob = (jobsData?.content ?? []).find(
        (j) => j.status === 'PENDING' || j.status === 'RUNNING' || j.status === 'AWAITING_RESULTS'
    );
    const sortLabelMap: Record<string, string> = {
        id: 'ID',
        word: '단어',
        updatedAt: '수정일',
        hitCount: '조회수',
        difficultyScore: '난이도',
    };

    const parseResult = (resultJson?: string) => {
        if (!resultJson) return {phase: '-', currentProvider: '-', heartbeatAt: '-'};
        try {
            const parsed = JSON.parse(resultJson) as { phase?: string; currentProvider?: string; heartbeatAt?: string };
            return {
                phase: parsed.phase || '-',
                currentProvider: parsed.currentProvider || '-',
                heartbeatAt: parsed.heartbeatAt || '-',
            };
        } catch {
            return {phase: '-', currentProvider: '-', heartbeatAt: '-'};
        }
    };

    useEffect(() => {
        if (!runningJob) return;
        const timer = setInterval(() => {
            refetchJobs();
            refetch();
        }, 10000);
        return () => clearInterval(timer);
    }, [runningJob, refetchJobs, refetch]);

    const handleEditOpen = (row: DictionaryWordItem) => {
        setEditRow(row);
        setEditTranslation(row.translation || '');
        setEditPos(row.pos || '');
        setEditDifficulty(row.difficultyScore ?? '');
        setEditOpen(true);
    };

    const handleEditSave = () => {
        if (!editRow) return;
        updateMutation.mutate(
            {
                id: editRow.id,
                data: {
                    translation: editTranslation || undefined,
                    pos: editPos || undefined,
                    difficultyScore: editDifficulty !== '' ? Number(editDifficulty) : undefined,
                },
            },
            {
                onSuccess: () => {
                    setEditOpen(false);
                    setEditRow(null);
                },
            }
        );
    };

    const handleSearch = () => {
        setPage(0);
        setWordFilter(wordInput.trim());
        setLanguageCodeFilter(languageCodeInput.trim());
    };

    const handleBackfill = () => {
        const limitInput = prompt('백필 대상 최대 row 수(limit)', '5000');
        if (limitInput === null) return;
        const batchSizeInput = prompt('AI 배치 크기(batchSize, 권장 20~50)', '50');
        if (batchSizeInput === null) return;

        const limit = parseInt(limitInput, 10);
        const batchSize = parseInt(batchSizeInput, 10);

        if (!Number.isFinite(limit) || limit <= 0) {
            alert('limit는 1 이상의 숫자여야 합니다.');
            return;
        }
        if (!Number.isFinite(batchSize) || batchSize <= 0) {
            alert('batchSize는 1 이상의 숫자여야 합니다.');
            return;
        }

        if (!confirm(`dictionary_word 백필 실행\nlimit=${limit}\nbatchSize=${batchSize}`)) {
            return;
        }

        setCreatedJob(null);
        backfill.mutate(
            {limit, batchSize},
            {
                onSuccess: (job) => {
                    setCreatedJob(job);
                    refetchJobs();
                },
            }
        );
    };

    const openRegenDialog = () => {
        setRegenDialogOpen(true);
        previewRegen.reset();
        previewRegen.mutate();   // dry-run: 후보수 + 샘플 로드
    };

    const handleRegenerate = () => {
        const limit = parseInt(regenLimit, 10);
        const batchSize = parseInt(regenBatchSize, 10);
        if (!Number.isFinite(limit) || limit <= 0) {
            alert('limit는 1 이상의 숫자여야 합니다.');
            return;
        }
        if (!Number.isFinite(batchSize) || batchSize <= 0) {
            alert('batchSize는 1 이상의 숫자여야 합니다.');
            return;
        }
        const candidateCount = previewRegen.data?.candidateCount ?? 0;
        if (!confirm(
            `영국식 → 미국식(GenAm) 발음기호 재생성 실행\n` +
            `현재 후보=${candidateCount}건\nlimit=${limit} (이번 실행 최대)\nbatchSize=${batchSize}\n\n` +
            `검증 통과한 미국식 값만 덮어쓰며, 나머지 메타데이터는 보존합니다.`
        )) {
            return;
        }
        setCreatedJob(null);
        regen.mutate(
            {limit, batchSize},
            {
                onSuccess: (job) => {
                    setCreatedJob(job);
                    setRegenDialogOpen(false);
                    refetchJobs();
                },
            }
        );
    };

    const formatDateTime = (value: string) => {
        return formatKstDateTime(value);
    };

    const renderShortText = (value: string | null, maxLength: number = 40) => {
        if (!value) return '-';
        const trimmed = value.trim();
        if (trimmed.length <= maxLength) return trimmed;
        const short = `${trimmed.slice(0, maxLength)}...`;
        return (
            <Tooltip title={trimmed} placement="top">
                <Box component="span" sx={{cursor: 'help'}}>
                    {short}
                </Box>
            </Tooltip>
        );
    };

    const dictionaryCsvColumns: CsvColumnDef[] = [
        {key: 'id', label: 'ID', readonly: true},
        {key: 'word', label: '단어', required: true},
        {key: 'language_code', label: '언어코드', required: true},
        {key: 'pos', label: '품사'},
        {key: 'translation', label: '번역'},
        {key: 'translation_lang', label: '번역언어'},
        {key: 'difficulty_score', label: '난이도'},
        {key: 'source_type', label: 'Source Type', readonly: true},
        {key: 'hit_count', label: '히트수', readonly: true},
    ];

    const handleCsvDownload = async () => {
        const limitInput = prompt('다운로드 최대 개수 (빈 값이면 전체)', '5000');
        if (limitInput === null) return;
        const limit = limitInput.trim() ? parseInt(limitInput.trim(), 10) : undefined;
        if (limit !== undefined && (!Number.isFinite(limit) || limit <= 0)) {
            alert('1 이상의 숫자를 입력해주세요.');
            return;
        }
        try {
            const items = await exportMutation.mutateAsync({
                word: wordFilter || undefined,
                languageCode: languageCodeFilter || undefined,
                onlyMissing,
                limit,
            }) ?? [];
            const csvHeaders = ['id', 'word', 'language_code', 'pos', 'translation', 'translation_lang', 'difficulty_score', 'source_type', 'hit_count'];
            const csvRows = items.map(item => ({
                id: item.id,
                word: item.word,
                language_code: item.languageCode,
                pos: item.pos ?? '',
                translation: item.translation ?? '',
                translation_lang: item.translationLang ?? '',
                difficulty_score: item.difficultyScore ?? '',
                source_type: item.sourceType ?? '',
                hit_count: item.hitCount,
            }));
            const csv = generateCsv(csvHeaders, csvRows);
            const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            downloadCsv(`dictionary_words_${date}.csv`, csv);
        } catch (e) {
            console.error('Export failed', e);
        }
    };

    const handleCsvImport = async (rows: Record<string, string>[]) => {
        const words = rows.map(row => ({
            id: row.id ? parseInt(row.id) : undefined,
            word: row.word,
            languageCode: row.language_code,
            pos: row.pos || undefined,
            translation: row.translation || undefined,
            translationLang: row.translation_lang || undefined,
            difficultyScore: row.difficulty_score ? parseFloat(row.difficulty_score) : undefined,
        }));
        const res = await upsertMutation.mutateAsync({words});
        return {
            total: res.totalRequested,
            inserted: res.insertedCount,
            updated: res.updatedCount,
            skipped: res.skippedCount,
        };
    };

    return (
        <Box sx={{p: 3}}>
            <Typography variant="h4" sx={{mb: 3}}>사전(Dictionary) 관리</Typography>

            {createdJob && (
                <Alert severity="info" sx={{mb: 2}}>
                    백필 작업이 생성되었습니다. jobId={createdJob.id}
                </Alert>
            )}
            {backfill.error && (
                <Alert severity="error" sx={{mb: 2}}>
                    백필 시작 실패: {backfill.error.message}
                </Alert>
            )}
            {cancelBackfill.error && (
                <Alert severity="error" sx={{mb: 2}}>
                    백필 중단 실패: {cancelBackfill.error.message}
                </Alert>
            )}

            <Card sx={{mb: 2}}>
                <CardContent>
                    <Stack spacing={2}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                            <Typography variant="subtitle1">조회 조건</Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Refresh/>}
                                onClick={() => refetch()}
                            >
                                목록 새로고침
                            </Button>
                        </Stack>

                        <Stack direction={{xs: 'column', md: 'row'}} spacing={1} alignItems={{xs: 'stretch', md: 'center'}}>
                            <TextField
                                label="단어 검색"
                                size="small"
                                value={wordInput}
                                onChange={(e) => setWordInput(e.target.value)}
                                sx={{minWidth: {md: 220}}}
                            />
                            <TextField
                                label="단어 언어 코드"
                                size="small"
                                placeholder="en"
                                value={languageCodeInput}
                                onChange={(e) => setLanguageCodeInput(e.target.value)}
                                sx={{width: {xs: '100%', md: 160}}}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={onlyMissing}
                                        onChange={(_, checked) => {
                                            setOnlyMissing(checked);
                                            setPage(0);
                                        }}
                                    />
                                }
                                label="누락 메타데이터만"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={backfillExhausted}
                                        onChange={(_, checked) => {
                                            setBackfillExhausted(checked);
                                            setPage(0);
                                        }}
                                    />
                                }
                                label="백필 소진만"
                            />
                            <FormControl size="small" sx={{minWidth: 150}}>
                                <InputLabel>Source Type</InputLabel>
                                <Select
                                    value={sourceType}
                                    label="Source Type"
                                    onChange={(e) => {
                                        setSourceType(e.target.value);
                                        setPage(0);
                                    }}
                                >
                                    <MenuItem value="">전체</MenuItem>
                                    <MenuItem value="UNKNOWN">UNKNOWN</MenuItem>
                                    <MenuItem value="TERM_GEN">TERM_GEN</MenuItem>
                                    <MenuItem value="IMAGE_EXTRACT">IMAGE_EXTRACT</MenuItem>
                                    <MenuItem value="FREE_DICTIONARY">FREE_DICTIONARY</MenuItem>
                                    <MenuItem value="BACKFILL">BACKFILL</MenuItem>
                                    <MenuItem value="MANUAL">MANUAL</MenuItem>
                                    <MenuItem value="USER_INPUT">USER_INPUT</MenuItem>
                                </Select>
                            </FormControl>
                            <Button variant="contained" onClick={handleSearch} sx={{ml: {md: 'auto'}}}>
                                조회
                            </Button>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="body2" color="text.secondary">정렬</Typography>
                            <FormControl size="small" sx={{minWidth: 140}}>
                                <InputLabel>기준</InputLabel>
                                <Select
                                    value={sortField}
                                    label="기준"
                                    onChange={(e) => {
                                        setSortField(e.target.value);
                                        setPage(0);
                                    }}
                                >
                                    <MenuItem value="id">ID</MenuItem>
                                    <MenuItem value="word">단어</MenuItem>
                                    <MenuItem value="updatedAt">수정일</MenuItem>
                                    <MenuItem value="hitCount">조회수</MenuItem>
                                    <MenuItem value="difficultyScore">난이도</MenuItem>
                                </Select>
                            </FormControl>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    setSortDirection(prev => prev === 'ASC' ? 'DESC' : 'ASC');
                                    setPage(0);
                                }}
                                title={sortDirection === 'ASC' ? '오름차순' : '내림차순'}
                            >
                                {sortDirection === 'ASC' ? <ArrowUpward fontSize="small"/> : <ArrowDownward fontSize="small"/>}
                            </IconButton>
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`${sortLabelMap[sortField] ?? sortField} · ${sortDirection}`}
                            />
                        </Stack>

                        <Divider/>

                        <Stack direction={{xs: 'column', md: 'row'}} spacing={1} justifyContent="space-between" alignItems={{xs: 'stretch', md: 'center'}}>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Button
                                    variant="outlined"
                                    startIcon={<Download/>}
                                    onClick={handleCsvDownload}
                                    disabled={exportMutation.isPending}
                                >
                                    CSV 다운로드
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<Upload/>}
                                    onClick={() => setCsvDialogOpen(true)}
                                >
                                    CSV 업로드
                                </Button>
                            </Stack>

                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<Public/>}
                                    disabled={regen.isPending || !!runningJob}
                                    onClick={openRegenDialog}
                                >
                                    발음기호 미국식 재생성
                                </Button>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<Build/>}
                                    disabled={backfill.isPending || !!runningJob}
                                    onClick={handleBackfill}
                                >
                                    {backfill.isPending ? '백필 시작 중...' : '메타데이터 백필 실행'}
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            <Card sx={{mb: 2}}>
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 1}}>
                        <Typography variant="subtitle1">Dictionary 백필 작업 (진행중)</Typography>
                        <Stack direction="row" spacing={1}>
                            <Button size="small" startIcon={<Refresh/>} onClick={() => refetchJobs()}>
                                새로고침
                            </Button>
                            <Button
                                size="small"
                                onClick={() => navigate('/batch/jobs?jobType=DICTIONARY_METADATA_BACKFILL&scopeType=GLOBAL')}
                            >
                                더보기
                            </Button>
                        </Stack>
                    </Stack>
                    {jobsLoading && <Typography variant="body2" color="text.secondary">불러오는 중...</Typography>}
                    {jobsError && <Alert severity="error">{jobsError.message}</Alert>}

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Job ID</TableCell>
                                <TableCell>상태</TableCell>
                                <TableCell>요청/대상/처리</TableCell>
                                <TableCell>성공/실패</TableCell>
                                <TableCell>배치수</TableCell>
                                <TableCell>진행 상태</TableCell>
                                <TableCell>시작/종료</TableCell>
                                <TableCell align="center">작업</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(jobsData?.content ?? []).map((job) => {
                                const result = parseResult(job.resultJson);
                                return (
                                <TableRow key={job.id}>
                                    <TableCell>{job.id}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={job.status}
                                            color={
                                                job.status === 'SUCCEEDED' ? 'success' :
                                                job.status === 'FAILED' ? 'error' :
                                                job.status === 'CANCELED' ? 'default' :
                                                job.status === 'AWAITING_RESULTS' ? 'info' : 'warning'
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>{job.requestedLimit}/{job.targetCount}/{job.processedCount}</TableCell>
                                    <TableCell>{job.successCount}/{job.failedCount}</TableCell>
                                    <TableCell>{job.batchCount}</TableCell>
                                    <TableCell>
                                        <Typography variant="caption" display="block">{result.phase}</Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">{result.currentProvider}</Typography>
                                        <Typography variant="caption" color="text.secondary">{formatDateTime(result.heartbeatAt)}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" display="block">
                                            {formatDateTime(job.startedAt || '')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDateTime(job.finishedAt || '')}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        {(job.status === 'PENDING' || job.status === 'RUNNING' || job.status === 'AWAITING_RESULTS') && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="error"
                                                startIcon={<Stop/>}
                                                disabled={cancelBackfill.isPending}
                                                onClick={() => {
                                                    if (!confirm(`백필 작업 #${job.id} 를 중단할까요?`)) return;
                                                    cancelBackfill.mutate(job.id, {
                                                        onSuccess: () => refetchJobs(),
                                                    });
                                                }}
                                            >
                                                중단
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )})}
                            {(jobsData?.content ?? []).length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{py: 2}}>
                                        <Typography variant="body2" color="text.secondary">
                                            현재 진행중인 작업이 없습니다.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {isLoading && <CircularProgress/>}
            {error && <Alert severity="error">{error.message}</Alert>}

            {data && (
                <Card>
                    <CardContent sx={{p: 0}}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>단어</TableCell>
                                    <TableCell>언어</TableCell>
                                    <TableCell>POS</TableCell>
                                    <TableCell>번역</TableCell>
                                    <TableCell>번역언어</TableCell>
                                    <TableCell align="right">난이도</TableCell>
                                    <TableCell>AI Provider</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell align="right">Hit</TableCell>
                                    <TableCell>Updated At</TableCell>
                                    <TableCell>상태</TableCell>
                                    <TableCell align="right">백필시도</TableCell>
                                    <TableCell align="center">수정</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(data.content ?? []).map((row) => (
                                    <TableRow key={row.id} hover>
                                        <TableCell>{row.id}</TableCell>
                                        <TableCell><strong>{row.word}</strong></TableCell>
                                        <TableCell>{row.languageCode}</TableCell>
                                        <TableCell>{row.pos ?? '-'}</TableCell>
                                        <TableCell>{renderShortText(row.translation, 36)}</TableCell>
                                        <TableCell>{row.translationLang ?? '-'}</TableCell>
                                        <TableCell align="right">{row.difficultyScore?.toFixed(1) ?? '-'}</TableCell>
                                        <TableCell>{row.aiProvider ?? '-'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={row.sourceType || 'N/A'}
                                                size="small"
                                                color={
                                                    row.sourceType === 'IMAGE_EXTRACT' ? 'error' :
                                                    row.sourceType === 'UNKNOWN' ? 'warning' :
                                                    row.sourceType === 'USER_INPUT' ? 'warning' :
                                                    row.sourceType === 'MANUAL' ? 'success' :
                                                    row.sourceType === 'TERM_GEN' ? 'primary' :
                                                    row.sourceType === 'FREE_DICTIONARY' ? 'info' :
                                                    'default'
                                                }
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="right">{row.hitCount}</TableCell>
                                        <TableCell>{formatDateTime(row.updatedAt)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={row.isMissingMetadata ? 'MISSING' : 'READY'}
                                                color={row.isMissingMetadata ? 'warning' : 'success'}
                                                variant={row.isMissingMetadata ? 'outlined' : 'filled'}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: (row.backfillAttemptCount ?? 0) >= 3 ? 'bold' : 'normal',
                                                    color: (row.backfillAttemptCount ?? 0) >= 3 ? 'error.main' : 'text.primary',
                                                }}
                                            >
                                                {row.backfillAttemptCount ?? 0}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={0.5} justifyContent="center">
                                                <IconButton size="small" onClick={() => handleEditOpen(row)}>
                                                    <EditIcon fontSize="small"/>
                                                </IconButton>
                                                {(row.backfillAttemptCount ?? 0) > 0 && (
                                                    <Tooltip title="백필 시도 횟수 리셋">
                                                        <IconButton
                                                            size="small"
                                                            color="warning"
                                                            disabled={resetBackfillMutation.isPending}
                                                            onClick={() => {
                                                                if (!confirm(`#${row.id} ${row.word} 의 백필 시도 횟수를 0으로 리셋할까요?`)) return;
                                                                resetBackfillMutation.mutate(row.id, {
                                                                    onSuccess: () => refetch(),
                                                                });
                                                            }}
                                                        >
                                                            <RotateLeft fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {(data.content ?? []).length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={15} align="center" sx={{py: 3}}>
                                            <Typography variant="body2" color="text.secondary">
                                                조회된 데이터가 없습니다.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>

                        <TablePagination
                            component="div"
                            count={data.totalCount ?? 0}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={size}
                            onRowsPerPageChange={(e) => {
                                setSize(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[20, 50, 100]}
                        />
                    </CardContent>
                </Card>
            )}

            <CsvUploadDialog
                open={csvDialogOpen}
                onClose={() => setCsvDialogOpen(false)}
                title="사전 단어 CSV 업로드"
                columns={dictionaryCsvColumns}
                onImport={handleCsvImport}
                isLoading={upsertMutation.isPending}
            />

            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>사전 항목 수정</DialogTitle>
                <DialogContent>
                    <Box sx={{mt: 1, display: 'flex', flexDirection: 'column', gap: 2}}>
                        <TextField label="단어" value={editRow?.word || ''} disabled fullWidth size="small"/>
                        <TextField label="언어" value={editRow?.languageCode || ''} disabled fullWidth size="small"/>
                        <TextField
                            label="번역"
                            value={editTranslation}
                            onChange={(e) => setEditTranslation(e.target.value)}
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="품사 (POS)"
                            value={editPos}
                            onChange={(e) => setEditPos(e.target.value)}
                            fullWidth
                            size="small"
                            placeholder="NOUN, VERB, ADJ, ADV..."
                        />
                        <TextField
                            label="난이도 (0-100)"
                            type="number"
                            value={editDifficulty}
                            onChange={(e) => setEditDifficulty(e.target.value === '' ? '' : Number(e.target.value))}
                            fullWidth
                            size="small"
                        />
                        <Typography variant="caption" color="text.secondary">
                            수동 수정 시 source_type이 MANUAL로 자동 변경됩니다.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)}>취소</Button>
                    <Button onClick={handleEditSave} variant="contained" disabled={updateMutation.isPending}>
                        저장
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={regenDialogOpen} onClose={() => setRegenDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>발음기호 미국식(GenAm) 재생성</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{mt: 1}}>
                        <Alert severity="info">
                            영국식(RP) 영어 발음기호를 미국식으로 재생성합니다. 검증을 통과한 값만 덮어쓰며,
                            phonetic 외 메타데이터(뜻/품사/난이도)는 보존됩니다. 자동 일일 백필에는 포함되지 않는 수동 작업입니다.
                            먼저 limit를 작게(canary) 실행해 결과를 확인한 뒤 전체(20000)를 실행하세요.
                        </Alert>

                        <Box>
                            <Typography variant="subtitle2" sx={{mb: 1}}>대상 미리보기 (dry-run)</Typography>
                            {previewRegen.isPending && (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <CircularProgress size={18}/>
                                    <Typography variant="body2" color="text.secondary">후보 조회 중...</Typography>
                                </Stack>
                            )}
                            {previewRegen.error && (
                                <Alert severity="error">미리보기 실패: {previewRegen.error.message}</Alert>
                            )}
                            {previewRegen.data && (
                                <>
                                    <Typography variant="body2" sx={{mb: 1}}>
                                        영국식 후보 <strong>{previewRegen.data.candidateCount.toLocaleString()}</strong>건
                                        (샘플 {(previewRegen.data.samples ?? []).length}개)
                                    </Typography>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>ID</TableCell>
                                                <TableCell>단어</TableCell>
                                                <TableCell>품사</TableCell>
                                                <TableCell>현재(영국식) 발음기호</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(previewRegen.data.samples ?? []).slice(0, 20).map((s) => (
                                                <TableRow key={s.id}>
                                                    <TableCell>{s.id}</TableCell>
                                                    <TableCell>{s.word}</TableCell>
                                                    <TableCell>{s.pos ?? '-'}</TableCell>
                                                    <TableCell>{s.phonetic ?? '-'}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </>
                            )}
                        </Box>

                        <Divider/>

                        <Stack direction="row" spacing={1}>
                            <TextField
                                label="limit (이번 실행 최대)"
                                size="small"
                                value={regenLimit}
                                onChange={(e) => setRegenLimit(e.target.value)}
                                helperText="canary는 작게(예 200), 전체는 20000"
                                sx={{minWidth: 220}}
                            />
                            <TextField
                                label="batchSize"
                                size="small"
                                value={regenBatchSize}
                                onChange={(e) => setRegenBatchSize(e.target.value)}
                                helperText="권장 50"
                                sx={{width: 140}}
                            />
                        </Stack>
                        {regen.error && <Alert severity="error">재생성 시작 실패: {regen.error.message}</Alert>}
                        {!!runningJob && (
                            <Alert severity="warning">진행 중인 사전 백필 작업이 있어 실행할 수 없습니다.</Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRegenDialogOpen(false)}>취소</Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        disabled={regen.isPending || !!runningJob || previewRegen.isPending}
                        onClick={handleRegenerate}
                    >
                        {regen.isPending ? '시작 중...' : '재생성 실행'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
